import {
  parseJackrabbitAgeDurationToMonths,
  formatClassAgeRange,
} from "helpers/dataConversion/convertClassAgeRange";
import {
  DEFAULT_CLASS_SCHEDULE_FILTER,
  filterAndSortClassRows,
} from "./classScheduleFilter";
import { buildProgramScheduleUrl } from "./scheduleFilterSearchParams";

/** Programs parents can search from the home finder (excludes Competitive). */
export const HOME_FINDER_PROGRAMS = ["Preschool", "Recreational"];

/** Where to send parents when age is outside a program's schedule range. */
export const PROGRAM_AGE_REDIRECTS = {
  Preschool: { too_old: "Recreational", too_young: null },
  Recreational: { too_young: "Preschool", too_old: null },
};

/**
 * Pick the best starting program from child age before live schedule data loads.
 * Program pages refine this with Jackrabbit bounds + redirect messaging.
 */
export const resolveProgramForChildAge = (childYears, childMonths = 0) => {
  if (childYears === null || childYears === undefined || childYears === "") {
    return "Recreational";
  }

  const totalMonths = Number(childYears) * 12 + Number(childMonths || 0);
  if (Number.isNaN(totalMonths)) return "Recreational";

  if (totalMonths < 72) return "Preschool";
  return "Recreational";
};

export const buildProgramFilterUrl = (programName, filter) =>
  buildProgramScheduleUrl(programName, filter);

/** @returns {{ minMonths: number|null, maxMonths: number|null }|null} */
export const getProgramAgeBounds = (rows) => {
  const source = Array.isArray(rows) ? rows : [];
  if (source.length === 0) return null;

  let minMonths = null;
  let maxMonths = null;

  source.forEach((row) => {
    const min = parseJackrabbitAgeDurationToMonths(row.min_age);
    const max = parseJackrabbitAgeDurationToMonths(row.max_age);
    if (min !== null) {
      minMonths = minMonths === null ? min : Math.min(minMonths, min);
    }
    if (max !== null) {
      maxMonths = maxMonths === null ? max : Math.max(maxMonths, max);
    }
  });

  if (minMonths === null && maxMonths === null) return null;
  return { minMonths, maxMonths };
};

const monthsToJackrabbitDuration = (totalMonths) => {
  if (totalMonths === null || totalMonths === undefined) return null;

  const years = Math.floor(totalMonths / 12);
  const months = totalMonths % 12;
  return `P${String(years).padStart(2, "0")}Y${String(months).padStart(2, "0")}M`;
};

/** Human-readable age pill for program overview, derived from live Jackrabbit rows. */
export const formatProgramAgeLabelFromRows = (rows) => {
  const bounds = getProgramAgeBounds(rows);
  if (!bounds) return null;

  return formatClassAgeRange(
    monthsToJackrabbitDuration(bounds.minMonths),
    monthsToJackrabbitDuration(bounds.maxMonths)
  );
};

/** @returns {"too_young"|"too_old"|null} */
export const getAgeDirection = (childYears, childMonths, bounds) => {
  if (
    childYears === null ||
    childYears === undefined ||
    childYears === "" ||
    !bounds
  ) {
    return null;
  }

  const childTotalMonths =
    Number(childYears) * 12 + Number(childMonths || 0);
  if (Number.isNaN(childTotalMonths)) return null;

  if (bounds.minMonths !== null && childTotalMonths < bounds.minMonths) {
    return "too_young";
  }
  if (bounds.maxMonths !== null && childTotalMonths > bounds.maxMonths) {
    return "too_old";
  }

  return null;
};

export const formatProgramAgeRange = (bounds) => {
  if (!bounds) return null;

  const { minMonths, maxMonths } = bounds;
  if (minMonths === null && maxMonths === null) return null;

  const minYears = minMonths !== null ? Math.floor(minMonths / 12) : null;
  const maxYears = maxMonths !== null ? Math.floor(maxMonths / 12) : null;

  if (minYears !== null && maxYears !== null) {
    const maxIsYearBoundary =
      maxMonths % 12 >= 11 || maxMonths % 12 === 0;
    if (minMonths % 12 === 0 && maxIsYearBoundary) {
      if (minYears === maxYears) return `age ${minYears}`;
      return `ages ${minYears}–${maxYears}`;
    }
  }

  if (minYears !== null && maxYears !== null) {
    return `ages ${minYears}–${maxYears}`;
  }
  if (minYears !== null) return `ages ${minYears}+`;
  if (maxYears !== null) return `up to age ${maxYears}`;
  return null;
};

export const formatChildAgeLabel = (years, months = 0) => {
  if (years === null || years === undefined || years === "") return "this age";

  const monthCount = Number(months || 0);
  if (monthCount === 0) {
    return years === 1 ? "a 1-year-old" : `a ${years}-year-old`;
  }

  return `a ${years}-year-old, ${monthCount} month${monthCount === 1 ? "" : "s"}`;
};

export const getProgramLinkLabel = (programName) =>
  programName === "Tumbling" ? "Tumbling" : `${programName} Gymnastics`;

/**
 * When selected age is outside this program's classes, return redirect context.
 * @returns {object|null}
 */
export const getAgeMismatchContext = (programName, filter, rows) => {
  if (filter?.childAgeYears === null || filter?.childAgeYears === undefined) {
    return null;
  }

  const bounds = getProgramAgeBounds(rows);
  if (!bounds) return null;

  const direction = getAgeDirection(
    filter.childAgeYears,
    filter.childAgeMonths,
    bounds
  );
  if (!direction) return null;

  const suggestedProgram = PROGRAM_AGE_REDIRECTS[programName]?.[direction];
  if (!suggestedProgram) return null;

  return {
    direction,
    bounds,
    suggestedProgram,
    childAgeYears: filter.childAgeYears,
    childAgeMonths: filter.childAgeMonths ?? 0,
    ageRangeLabel: formatProgramAgeRange(bounds),
    suggestedProgramLabel: getProgramLinkLabel(suggestedProgram),
    suggestedProgramUrl: buildProgramFilterUrl(suggestedProgram, filter),
  };
};

/**
 * Why filters returned zero matches.
 * @returns {"age_out_of_program"|"other_filters"|"no_matches"}
 */
export const getFilterEmptyReason = (programName, filter, rows) => {
  const ageMismatch = getAgeMismatchContext(programName, filter, rows);
  if (ageMismatch) return { type: "age_out_of_program", ...ageMismatch };

  if (filter?.childAgeYears !== null && filter?.childAgeYears !== undefined) {
    const ageOnlyFilter = {
      ...DEFAULT_CLASS_SCHEDULE_FILTER,
      childAgeYears: filter.childAgeYears,
      childAgeMonths: filter.childAgeMonths ?? 0,
    };
    const { matchCount } = filterAndSortClassRows(rows, ageOnlyFilter);
    if (matchCount > 0) return { type: "other_filters" };
  }

  return { type: "no_matches" };
};
